import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Modal, Button, List, Tag, Space, Empty, message, Popover, InputNumber } from 'antd';
import {
  BellOutlined,
  CalendarOutlined,
  ArrowLeftOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import moment from 'moment-jalaali';
import { useAuth } from '../../contexts/AuthContext';
import { notificationsApi } from '../../utils/api';
import {
  dedupeReminders,
  getReminderKey,
  normalizeReminderList,
} from '../../utils/followUpReminderUtils';

const NOTIFICATION_SOUND = '/images/mixkit-software-interface-start-2574.wav';
const POLL_INTERVAL_MS = 30000;
const SHOWN_KEY = 'crm_follow_up_reminder_shown_ids';
const SNOOZE_KEY = 'crm_follow_up_reminder_snooze_until';
const SNOOZE_PRESETS = [5, 10, 15, 30];

const readShownKeys = () => {
  try {
    const raw = sessionStorage.getItem(SHOWN_KEY);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch {
    return new Set();
  }
};

const writeShownKeys = (set) => {
  try {
    sessionStorage.setItem(SHOWN_KEY, JSON.stringify([...set]));
  } catch {
    // ignore
  }
};

const readSnoozeUntilMap = () => {
  try {
    const raw = sessionStorage.getItem(SNOOZE_KEY);
    const obj = raw ? JSON.parse(raw) : {};
    return obj && typeof obj === 'object' ? obj : {};
  } catch {
    return {};
  }
};

const writeSnoozeUntilMap = (map) => {
  try {
    sessionStorage.setItem(SNOOZE_KEY, JSON.stringify(map));
  } catch {
    // ignore
  }
};

const formatEntityLabel = (entityType) => {
  if (entityType === 'deal') return 'معامله';
  if (entityType === 'lead') return 'سرنخ';
  return entityType || '—';
};

const formatJalaliDateTime = (value) => {
  if (value == null || value === '') return null;
  try {
    const raw = String(value).trim();
    if (!raw) return null;
    let parsed = moment(raw);
    if (!parsed.isValid()) {
      parsed = moment(raw.replace(/([+-]\d{2}:?\d{2})$/, ''));
    }
    if (!parsed.isValid()) return raw;
    const hasTime = parsed.hour() !== 0 || parsed.minute() !== 0 || parsed.second() !== 0;
    return parsed.format(hasTime ? 'jYYYY/jMM/jDD HH:mm:ss' : 'jYYYY/jMM/jDD');
  } catch {
    return String(value);
  }
};

const formatRemindLabel = (item) => {
  const time = item.remind_time;
  const type = item.remind_type;
  const typeMap = { minute: 'دقیقه', hour: 'ساعت', day: 'روز' };
  if (!time) return null;
  return `${time} ${typeMap[type] || type || ''} قبل`;
};

const resolveReminderPath = (item) => {
  if (!item) return null;
  const nav = item.navigation || {};
  const entityId = nav.entity_id ?? item.entity_id;
  if (entityId == null || entityId === '') return null;

  const tab = nav.tab || 'follow-up';
  const followUpId = nav.follow_up_id ?? item.follow_up_id;
  const isDeal = nav.screen === 'deal_detail' || item.entity_type === 'deal';
  const isLead = nav.screen === 'lead_detail' || item.entity_type === 'lead';

  let path = null;
  if (isDeal) path = `/deals/${entityId}?tab=${encodeURIComponent(tab)}`;
  else if (isLead) path = `/leads/${entityId}?tab=${encodeURIComponent(tab)}`;
  if (!path) return null;

  if (followUpId != null && followUpId !== '') {
    path += `&follow_up_id=${encodeURIComponent(followUpId)}`;
  }
  return path;
};

const openReminderInNewTab = (item) => {
  const path = resolveReminderPath(item);
  if (!path) {
    console.warn('Follow-up reminder: cannot resolve path', item);
    return false;
  }
  const url = `${window.location.origin}${path}`;
  const opened = window.open(url, '_blank', 'noopener,noreferrer');
  if (!opened) {
    window.location.assign(url);
  }
  return true;
};

const SnoozePopoverContent = ({ onConfirm, loading }) => {
  const [minutes, setMinutes] = useState(5);

  return (
    <div className="custom-font w-[200px] space-y-2" onClick={(e) => e.stopPropagation()}>
      <div className="text-xs text-gray-500 mb-1">تعویق یادآوری (دقیقه)</div>
      <div className="flex flex-wrap gap-1">
        {SNOOZE_PRESETS.map((m) => (
          <Button
            key={m}
            size="small"
            type={minutes === m ? 'primary' : 'default'}
            onClick={() => setMinutes(m)}
            className="custom-font"
          >
            {m}
          </Button>
        ))}
      </div>
      <InputNumber
        min={1}
        max={1440}
        value={minutes}
        onChange={(v) => setMinutes(v || 1)}
        addonAfter="دقیقه"
        style={{ width: '100%' }}
        className="custom-font"
      />
      <Button
        type="primary"
        block
        size="small"
        loading={loading}
        icon={<ClockCircleOutlined />}
        onClick={() => onConfirm(minutes)}
        className="custom-font"
      >
        تعویق
      </Button>
    </div>
  );
};

const FollowUpReminderPoller = () => {
  const { isAuthenticated, token } = useAuth();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snoozingKey, setSnoozingKey] = useState(null);
  const [snoozeOpenKey, setSnoozeOpenKey] = useState(null);
  const shownKeysRef = useRef(readShownKeys());
  const snoozeUntilRef = useRef(readSnoozeUntilMap());
  const audioRef = useRef(null);
  const pollingRef = useRef(false);
  const lastDisplayedSigRef = useRef('');

  const playSound = useCallback(() => {
    try {
      if (!audioRef.current) {
        audioRef.current = new Audio(NOTIFICATION_SOUND);
      }
      audioRef.current.currentTime = 0;
      const playPromise = audioRef.current.play();
      if (playPromise?.catch) {
        playPromise.catch(() => {});
      }
    } catch {
      // autoplay may be blocked
    }
  }, []);

  const isSnoozed = useCallback((key, now = Date.now()) => {
    const until = snoozeUntilRef.current[key];
    if (until == null) return false;
    if (now < Number(until)) return true;
    delete snoozeUntilRef.current[key];
    writeSnoozeUntilMap(snoozeUntilRef.current);
    shownKeysRef.current.delete(key);
    writeShownKeys(shownKeysRef.current);
    return false;
  }, []);

  const filterPendingReminders = useCallback(
    (list) => {
      const now = Date.now();
      return dedupeReminders(list).filter((item) => {
        const key = getReminderKey(item);
        if (!key) return false;
        if (isSnoozed(key, now)) return false;
        return !shownKeysRef.current.has(key);
      });
    },
    [isSnoozed],
  );

  const pollReminders = useCallback(async () => {
    if (!isAuthenticated || !token || pollingRef.current) return;

    pollingRef.current = true;
    try {
      const res = await notificationsApi.getFollowUpReminders();
      const list = normalizeReminderList(res);
      if (!list.length) return;

      const pending = filterPendingReminders(list);
      if (!pending.length) return;

      const sig = pending.map((item) => getReminderKey(item)).join('|');
      const isNewBatch = sig !== lastDisplayedSigRef.current;

      setItems(pending);
      setOpen(true);
      if (isNewBatch) {
        lastDisplayedSigRef.current = sig;
        playSound();
      }
    } catch (error) {
      console.warn('Follow-up reminders poll failed:', error);
    } finally {
      pollingRef.current = false;
    }
  }, [isAuthenticated, token, playSound, filterPendingReminders]);

  useEffect(() => {
    if (!isAuthenticated || !token) return undefined;

    pollReminders();
    const interval = setInterval(pollReminders, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [isAuthenticated, token, pollReminders]);

  const markShown = (keys) => {
    keys.forEach((key) => shownKeysRef.current.add(key));
    writeShownKeys(shownKeysRef.current);
  };

  const removeItemFromModal = (key) => {
    setItems((prev) => {
      const next = prev.filter((item) => getReminderKey(item) !== key);
      if (next.length === 0) {
        setOpen(false);
        lastDisplayedSigRef.current = '';
      }
      return next;
    });
  };

  const handleDismiss = async () => {
    const keys = items.map((item) => getReminderKey(item)).filter(Boolean);
    markShown(keys);
    setOpen(false);
    setItems([]);
    lastDisplayedSigRef.current = '';
    setLoading(true);
    try {
      await notificationsApi.markAllAsRead();
    } catch (e) {
      console.warn('mark all read failed:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleView = (item) => {
    const opened = openReminderInNewTab(item);
    if (!opened) {
      message.error('مسیر این فعالیت پیدا نشد');
      return;
    }

    const key = getReminderKey(item);
    markShown([key]);
    removeItemFromModal(key);

    if (item.id) {
      notificationsApi.markAsRead(item.id).catch((e) => {
        console.warn('mark read failed:', e);
      });
    }
  };

  const handleSnooze = async (item, delayMinutes) => {
    const minutes = Number(delayMinutes);
    const key = getReminderKey(item);
    if (!key || !item?.id || !Number.isFinite(minutes) || minutes < 1) {
      message.warning('لطفاً تعداد دقیقه معتبر وارد کنید');
      return;
    }

    setSnoozingKey(key);
    try {
      await notificationsApi.snooze(item.id, minutes);
      snoozeUntilRef.current[key] = Date.now() + minutes * 60 * 1000;
      writeSnoozeUntilMap(snoozeUntilRef.current);
      shownKeysRef.current.add(key);
      writeShownKeys(shownKeysRef.current);
      removeItemFromModal(key);
      setSnoozeOpenKey(null);
      message.success(`یادآوری تا ${minutes} دقیقه دیگر به تعویق افتاد`);
    } catch (e) {
      console.warn('snooze failed:', e);
      message.error(e?.message || 'خطا در تعویق یادآوری');
    } finally {
      setSnoozingKey(null);
    }
  };

  return (
    <Modal
      open={open}
      title={
        <Space>
          <BellOutlined className="text-amber-500" />
          <span className="custom-font">یادآور پیگیری</span>
          {items.length > 1 ? (
            <Tag color="orange" className="m-0 custom-font">{items.length} مورد</Tag>
          ) : null}
        </Space>
      }
      onCancel={handleDismiss}
      footer={
        <Space>
          <Button onClick={handleDismiss} loading={loading} className="custom-font">
            متوجه شدم
          </Button>
          {items[0] ? (
            <Button
              type="primary"
              icon={<ArrowLeftOutlined />}
              onClick={() => handleView(items[0])}
              className="custom-font"
            >
              مشاهده فعالیت
            </Button>
          ) : null}
        </Space>
      }
      width={520}
      className="follow-up-reminder-modal"
      destroyOnClose
    >
      {items.length ? (
        <List
          size="small"
          rowKey={(item) => getReminderKey(item)}
          dataSource={items}
          renderItem={(item) => {
            const itemKey = getReminderKey(item);
            return (
              <List.Item
                className="custom-font"
                actions={[
                  <Popover
                    key="snooze"
                    trigger="click"
                    open={snoozeOpenKey === itemKey}
                    onOpenChange={(visible) => setSnoozeOpenKey(visible ? itemKey : null)}
                    content={
                      <SnoozePopoverContent
                        loading={snoozingKey === itemKey}
                        onConfirm={(mins) => handleSnooze(item, mins)}
                      />
                    }
                    placement="bottomLeft"
                  >
                    <Button
                      type="link"
                      size="small"
                      icon={<ClockCircleOutlined />}
                      loading={snoozingKey === itemKey}
                      className="custom-font"
                    >
                      تعویق
                    </Button>
                  </Popover>,
                  <Button
                    key="view"
                    type="link"
                    size="small"
                    icon={<ArrowLeftOutlined />}
                    onClick={() => handleView(item)}
                    className="custom-font"
                  >
                    مشاهده
                  </Button>,
                ]}
              >
                <List.Item.Meta
                  description={
                    <div className="space-y-1 text-xs text-gray-500 dark:text-gray-400">
                      <div>
                        <Tag className="m-0 text-[10px]">{formatEntityLabel(item.entity_type)}</Tag>
                        {(item.next_follow_up_date || item.follow_up_date) ? (
                          <span className="mr-2">
                            <CalendarOutlined className="ml-1" />
                            {formatJalaliDateTime(item.next_follow_up_date || item.follow_up_date)}
                          </span>
                        ) : null}
                      </div>
                      {formatRemindLabel(item) ? (
                        <div>زمان یادآوری: {formatRemindLabel(item)}</div>
                      ) : null}
                      {item.remark ? (
                        <div className="text-gray-600 dark:text-gray-300 whitespace-pre-wrap">{item.remark}</div>
                      ) : null}
                    </div>
                  }
                />
              </List.Item>
            );
          }}
        />
      ) : (
        <Empty description="یادآوری جدیدی نیست" />
      )}
    </Modal>
  );
};

export default FollowUpReminderPoller;
